
import React, { useEffect, useState, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { Empty, Select, Button, Form, Modal, Input, message } from 'antd'
import { getProductDetail } from '@services/productService'
import {
  findCategoryAttributesByIds,
  updateProductAttributeValues,
  FEATURE_IDS_KEY,
  ORIGINAL_FEATURE_IDS_KEY,
} from '@services/attributeService'
import CategorySelect from '@components/CategorySelect'
import { showError } from '@utils/alert'
import Loading from '@components/loading'
import './index.scss'

const { confirm } = Modal
const UNKNOWN_GROUP_KEY = '_unknown'

function ProductAttributeEdit(props: any) {
  const { id } = props || {}
  const [productId, setProductId] = useState<any>(id)
  const [categoryIds, setCategoryIds] = useState<any>(null)
  const [categoryAttributes, setCategoryAttributes] = useState<any>(null)
  const [productAttributes, setProductAttributes] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  
  const searchCategoryAttributeTemplate = useCallback((ids: string[], featureIds: string[]) => {
    findCategoryAttributesByIds(ids)
      .then(res => {
        setLoading(false)
        if (Array.isArray(res.data.items)) {
          setCategoryAttributes(res.data.items?.map(catAttr => catAttr.attr_set?.items || [])?.flat())

          if (Array.isArray(featureIds)) {
            const pMap = {}
            pMap[UNKNOWN_GROUP_KEY] = [...featureIds]
            // try to group by feature ids
            res.data.items.forEach(mapping => {
              mapping.attr_set?.items?.forEach(item => {
                const values: any[] = []
                item.childrens?.forEach(attrValue => {
                  const fIndex = featureIds.indexOf(attrValue.id)
                  // find the first feature id with item mapping
                  if (fIndex !== -1 && values.indexOf(attrValue.id) === -1) {
                    values.push(attrValue.id)
                    const removeIndex = pMap[UNKNOWN_GROUP_KEY].indexOf(attrValue.id)
                    if (removeIndex > -1) {
                      // remove it from unknown group
                      pMap[UNKNOWN_GROUP_KEY].splice(removeIndex, 1)
                    }
                  }
                })
                if (values.length) {
                  // selected attr values
                  pMap[item.id] = values
                }
              })
            })
            setProductAttributes(pMap)
          }
        }
      })
      .catch(e => {
        showError('error fetch category attributes mapping', e)
        console.error(e)
        setLoading(false)
      })
  }, [])

  const searchProductAttributes = useCallback(() => {
    if (!productId) return
    setLoading(true)
    setCategoryIds(null)
    setCategoryAttributes(null)
    setProductAttributes(null)
    getProductDetail(productId)
      .then(res => {
        if (res.data) {
          if (Array.isArray(res.data.categoryIds)) {
            setCategoryIds(res.data.categoryIds)
            searchCategoryAttributeTemplate(
              res.data.categoryIds,
              res.data.attributes?.attributes?.[FEATURE_IDS_KEY]?.choices || res.data.attributes?.attributes?.[ORIGINAL_FEATURE_IDS_KEY]?.choices
            )
          } else {
            setLoading(false)
          }
        }
      })
      .catch(e => {
        showError('error fetch product attributes info', e)
        console.error(e)
      })
  }, [productId, searchCategoryAttributeTemplate])

  useEffect(() => {
    if (id) {
      searchProductAttributes()
    }
  }, [id, searchProductAttributes])

  const onSaveProductAttributes = () => {
    if (!categoryAttributes || !categoryAttributes.length) return
    confirm({
      title: '更新商品属性',
      content: `确认更新商品: ${productId} 的关联属性吗？`,
      okText: '确认',
      okType: 'danger',
      cancelText: '取消',
      onOk() {
        const hide = message.loading('更新中...')
        let values = []
        Object.keys(productAttributes).forEach(itemId => {
          if (Array.isArray(productAttributes[itemId])) {
            values = values.concat(productAttributes[itemId])
          }
        })
        // console.log(values)
        updateProductAttributeValues(productId, values)
          .then(res => {
            hide()
            message.success(`更新商品:${productId} 跟属性项:${productId} 关联成功`)
            searchProductAttributes()
          })
          .catch(e => {
            hide()
            console.error(e)
            showError(e)
          })
      },
      onCancel() {
        
      },
    })
  }

  const renderCategoryAttributes = useCallback(() => {
    return categoryAttributes && categoryAttributes.length ? (
      categoryAttributes.map(item => {
        const selectedValues = productAttributes?.[item.id]
        return (
          <Form.Item
            label={item.display_name || item.value}
            tooltip={!item.valid ? '属性项被禁用' : (item.display_name || item.value)}
          >
            <Select
              showSearch
              disabled={!item.valid}
              mode={item.input === 0 ? 'multiple' : undefined} // 0 - multiple
              value={selectedValues}
              optionFilterProp="children"
              placeholder={!item.valid ? '该属性项被禁用' : '请选择属性项'}
              onChange={values => {
                const pMap = productAttributes ? Object.assign({}, productAttributes) : {}
                pMap[item.id] = Array.isArray(values) ? values : [values]
                setProductAttributes(pMap)
              }}
            >
              {item.childrens?.map(attrValue => {
                return (
                  <Select.Option
                    disabled={!attrValue.valid}
                    key={attrValue.id}
                    value={attrValue.id}
                    title={attrValue.value}
                  >
                    {attrValue.value}
                  </Select.Option>
                )
              })}
            </Select>
          </Form.Item>
        )
      })
    ) : null
  }, [categoryAttributes, productAttributes])

  return (
    <div>
      <Form layout="inline" name="product-attr">
        <Form.Item label="Product Id">
          <Input value={productId} onChange={e => setProductId(e.target.value)} style={{ width: '220px' }} placeholder="请输入商品 Id" disabled={id} />
        </Form.Item>
        <Form.Item>
          <Button disabled={id} type="primary" onClick={searchProductAttributes}>
            检索
          </Button>
        </Form.Item>
      </Form>
      <div className="p-attr-container">
        { loading ? <Loading spinning /> : null }
        {categoryIds ? (
          <Form.Item label="Category Ids" tooltip="分类限制关联属性集合">
            <CategorySelect disabled multiple value={categoryIds} placeholder=" " />
          </Form.Item>
        ): null}
        {renderCategoryAttributes() || <Empty description={categoryIds ? (
          <div>
            <strong><span>无分类属性关联: </span></strong>
            <Button size="small" onClick={() => window.open('/category-attribute')}>去添加</Button>
          </div>
        ) : null} />}
        {productAttributes?.[UNKNOWN_GROUP_KEY]?.length ? (
          <Form.Item label="UNKNOWN" tooltip="未知的分组, 分类属性中找不到对应/没有关联记录">
            <Select disabled value={productAttributes[UNKNOWN_GROUP_KEY]} />
          </Form.Item>
        ) : null}
        <Button style={{ marginTop: 12 }} onClick={onSaveProductAttributes}>
          保存
        </Button>
      </div>
    </div>
  )
}

function ProductAttributeEditFromPath() {
  const { id }: any = useParams()
  return (
    <div className="p-attributes">
      <ProductAttributeEdit id={id} />
    </div>
  )
}
export { ProductAttributeEdit }
export default ProductAttributeEditFromPath
